import { useEffect, useRef, useState, useCallback } from "react";
import {
  Pencil, Square, Circle, Slash, MoveUpRight,
  Eraser, Undo2, Redo2, Trash2, Eye, EyeOff, X, Highlighter,
} from "lucide-react";

// Epic-Pen-style annotation overlay for live teaching / screen sharing.
// - Full-screen transparent SVG drawn on top of EVERYTHING (z-index 9999).
// - Tools: freehand pen, highlighter (translucent), line, arrow, rectangle,
//   circle, eraser (per-stroke), undo / redo / clear, hide/show.
// - When the overlay is OFF, it's pointer-events: none so the underlying app
//   works normally. When ON, it captures pointer events.
// - Strokes are NOT persisted server-side — this is an ephemeral teaching
//   tool. Reload = blank canvas.

const COLORS = ["#dc2626", "#1d4ed8", "#15803d", "#f59e0b", "#a855f7", "#0f172a", "#ffffff"];
const PEN_WIDTHS = [2, 4, 7, 12];
const TOOLS = [
  { id: "pen",        icon: Pencil,      label: "Pen" },
  { id: "highlight",  icon: Highlighter, label: "Highlighter" },
  { id: "line",       icon: Slash,       label: "Line" },
  { id: "arrow",      icon: MoveUpRight, label: "Arrow" },
  { id: "rect",       icon: Square,      label: "Rectangle" },
  { id: "ellipse",    icon: Circle,      label: "Ellipse" },
  { id: "eraser",     icon: Eraser,      label: "Eraser" },
];

let _id = 0;
const nextId = () => (++_id);

export const PenOverlay = ({ open, onClose }) => {
  const [tool, setTool] = useState("pen");
  const [color, setColor] = useState("#dc2626");
  const [width, setWidth] = useState(4);
  const [shapes, setShapes] = useState([]);   // committed
  const [redoStack, setRedoStack] = useState([]);
  const [draft, setDraft] = useState(null);   // currently-being-drawn
  const [hidden, setHidden] = useState(false);
  const svgRef = useRef(null);
  const drawingRef = useRef(false);

  // Keep keyboard shortcuts: Z = undo, Shift+Z = redo, E = eraser, P = pen
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.target && /input|textarea|select/i.test(e.target.tagName)) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) doRedo();
        else doUndo();
        return;
      }
      if (e.key === "Escape") onClose?.();
      if (e.key.toLowerCase() === "p") setTool("pen");
      if (e.key.toLowerCase() === "e") setTool("eraser");
      if (e.key.toLowerCase() === "h") setTool("highlight");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, [open, shapes, redoStack]);

  const doUndo = useCallback(() => {
    setShapes(s => {
      if (!s.length) return s;
      const last = s[s.length - 1];
      setRedoStack(r => [...r, last]);
      return s.slice(0, -1);
    });
  }, []);
  const doRedo = useCallback(() => {
    setRedoStack(r => {
      if (!r.length) return r;
      const last = r[r.length - 1];
      setShapes(s => [...s, last]);
      return r.slice(0, -1);
    });
  }, []);
  const doClear = () => { setShapes([]); setRedoStack([]); };

  // ---- pointer handlers ----
  const ptFromEvent = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    const clientX = e.clientX ?? e.touches?.[0]?.clientX;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY;
    return [Math.round(clientX - rect.left), Math.round(clientY - rect.top)];
  };

  const onDown = (e) => {
    if (hidden) return;
    e.preventDefault();
    const [x, y] = ptFromEvent(e);
    if (tool === "eraser") {
      // erase whatever was under the click
      eraseAt(x, y);
      drawingRef.current = "erase";
      return;
    }
    drawingRef.current = true;
    if (tool === "pen" || tool === "highlight") {
      setDraft({
        id: nextId(),
        type: tool,
        color,
        width,
        opacity: tool === "highlight" ? 0.35 : 1,
        d: `M ${x} ${y}`,
      });
    } else {
      setDraft({ id: nextId(), type: tool, color, width, x1: x, y1: y, x2: x, y2: y });
    }
  };
  const onMove = (e) => {
    if (!drawingRef.current) return;
    const [x, y] = ptFromEvent(e);
    if (drawingRef.current === "erase") {
      eraseAt(x, y);
      return;
    }
    setDraft(d => {
      if (!d) return d;
      if (d.type === "pen" || d.type === "highlight") {
        return { ...d, d: `${d.d} L ${x} ${y}` };
      }
      return { ...d, x2: x, y2: y };
    });
  };
  const onUp = () => {
    if (drawingRef.current === "erase") { drawingRef.current = false; return; }
    if (draft) {
      setShapes(s => [...s, draft]);
      setRedoStack([]);
      setDraft(null);
    }
    drawingRef.current = false;
  };

  const eraseAt = (x, y) => {
    const r2 = 18 * 18;   // 18px radius eraser
    setShapes(s => s.filter(sh => !hitTest(sh, x, y, r2)));
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9998]"
      style={{ pointerEvents: hidden ? "none" : "auto" }}
      data-testid="pen-overlay"
    >
      {/* Drawing canvas */}
      {!hidden && (
        <svg
          ref={svgRef}
          className="absolute inset-0 w-full h-full"
          style={{ touchAction: "none", cursor: tool === "eraser" ? "cell" : "crosshair" }}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerLeave={onUp}
          data-testid="pen-canvas"
        >
          {shapes.map(s => <Shape key={s.id} s={s} />)}
          {draft && <Shape s={draft} />}
        </svg>
      )}

      {/* Floating toolbar — always at the top centre. Lives ABOVE the canvas
          so clicks on toolbar buttons don't draw. On phones, horizontal scroll
          keeps every tool reachable on narrow viewports. */}
      <div className="pointer-events-none fixed top-3 inset-x-0 flex justify-center z-[9999] px-2">
        <div
          className="pointer-events-auto inline-flex items-center gap-1 bg-card/95 backdrop-blur rounded-full shadow-xl border border-border px-1.5 py-1 max-w-full overflow-x-auto"
          style={{ scrollbarWidth: "none" }}
          data-testid="pen-toolbar"
        >
          {/* Tools */}
          {TOOLS.map(t => (
            <button
              key={t.id}
              onClick={() => setTool(t.id)}
              title={`${t.label}${t.id === "pen" ? " (P)" : t.id === "eraser" ? " (E)" : t.id === "highlight" ? " (H)" : ""}`}
              aria-label={t.label}
              aria-pressed={tool === t.id}
              data-testid={`pen-tool-${t.id}`}
              className={`w-8 h-8 rounded-full inline-flex items-center justify-center transition-colors ${
                tool === t.id ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:bg-muted"
              }`}
            >
              <t.icon className="w-4 h-4" />
            </button>
          ))}

          <span className="w-px h-6 bg-border mx-1" />

          {/* Colors */}
          {COLORS.map(c => (
            <button
              key={c}
              onClick={() => setColor(c)}
              aria-label={`Color ${c}`}
              data-testid={`pen-color-${c}`}
              className={`w-6 h-6 rounded-full border transition-transform ${color === c ? "scale-110 ring-2 ring-foreground/25 border-foreground/40" : "border-border hover:scale-105"}`}
              style={{ background: c }}
            />
          ))}

          <span className="w-px h-6 bg-border mx-1" />

          {/* Stroke width */}
          {PEN_WIDTHS.map(w => (
            <button
              key={w}
              onClick={() => setWidth(w)}
              aria-label={`Stroke ${w}px`}
              data-testid={`pen-width-${w}`}
              className={`w-8 h-8 rounded-full inline-flex items-center justify-center transition-colors ${width === w ? "bg-muted" : "hover:bg-muted"}`}
            >
              <span className="rounded-full bg-foreground" style={{ width: `${Math.min(16, w * 1.3)}px`, height: `${Math.min(16, w * 1.3)}px` }} />
            </button>
          ))}

          <span className="w-px h-6 bg-border mx-1" />

          {/* Actions */}
          <button onClick={doUndo} disabled={!shapes.length} title="Undo (Ctrl+Z)" data-testid="pen-undo-btn" aria-label="Undo" className="w-8 h-8 rounded-full inline-flex items-center justify-center text-foreground/70 hover:bg-muted disabled:opacity-30">
            <Undo2 className="w-4 h-4" />
          </button>
          <button onClick={doRedo} disabled={!redoStack.length} title="Redo (Ctrl+Shift+Z)" data-testid="pen-redo-btn" aria-label="Redo" className="w-8 h-8 rounded-full inline-flex items-center justify-center text-foreground/70 hover:bg-muted disabled:opacity-30">
            <Redo2 className="w-4 h-4" />
          </button>
          <button onClick={doClear} title="Clear all" data-testid="pen-clear-btn" aria-label="Clear" className="w-8 h-8 rounded-full inline-flex items-center justify-center text-foreground/70 hover:bg-destructive/10 hover:text-destructive">
            <Trash2 className="w-4 h-4" />
          </button>
          <button onClick={() => setHidden(h => !h)} title={hidden ? "Show annotations" : "Hide annotations (interact with page)"} data-testid="pen-visibility-btn" aria-label={hidden ? "Show" : "Hide"} className="w-8 h-8 rounded-full inline-flex items-center justify-center text-foreground/70 hover:bg-muted">
            {hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          </button>

          <span className="w-px h-6 bg-border mx-1" />

          <button
            onClick={onClose}
            title="Close pen overlay (Esc)"
            data-testid="pen-close-btn"
            aria-label="Close"
            className="w-8 h-8 rounded-full inline-flex items-center justify-center bg-destructive/10 text-destructive hover:bg-destructive/20"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Help footer */}
      <div className="pointer-events-none fixed bottom-3 inset-x-0 flex justify-center">
        <div className="bg-foreground/85 text-background text-[11px] px-3 py-1.5 rounded-full" data-testid="pen-help">
          Pen overlay is ON · {hidden ? "annotations hidden" : `${TOOLS.find(t => t.id === tool)?.label} · ${width}px`} · press Esc or the X to exit
        </div>
      </div>
    </div>
  );
};

const Shape = ({ s }) => {
  if (s.type === "pen" || s.type === "highlight") {
    return (
      <path
        d={s.d}
        stroke={s.color}
        strokeWidth={s.width}
        strokeOpacity={s.opacity ?? 1}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    );
  }
  if (s.type === "line") {
    return <line x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} stroke={s.color} strokeWidth={s.width} strokeLinecap="round" />;
  }
  if (s.type === "arrow") {
    const head = arrowHead(s);
    return (
      <g stroke={s.color} strokeWidth={s.width} fill="none" strokeLinecap="round" strokeLinejoin="round">
        <line x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} />
        <polyline points={head} fill={s.color} />
      </g>
    );
  }
  if (s.type === "rect") {
    const x = Math.min(s.x1, s.x2), y = Math.min(s.y1, s.y2);
    const w = Math.abs(s.x2 - s.x1), h = Math.abs(s.y2 - s.y1);
    return <rect x={x} y={y} width={w} height={h} stroke={s.color} strokeWidth={s.width} fill="none" />;
  }
  if (s.type === "ellipse") {
    const cx = (s.x1 + s.x2) / 2, cy = (s.y1 + s.y2) / 2;
    const rx = Math.abs(s.x2 - s.x1) / 2, ry = Math.abs(s.y2 - s.y1) / 2;
    return <ellipse cx={cx} cy={cy} rx={rx} ry={ry} stroke={s.color} strokeWidth={s.width} fill="none" />;
  }
  return null;
};

const arrowHead = (s) => {
  const dx = s.x2 - s.x1, dy = s.y2 - s.y1;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  const ux = dx / len, uy = dy / len;
  const headLen = Math.max(10, s.width * 3);
  const px = -uy, py = ux;
  const ax = s.x2 - ux * headLen, ay = s.y2 - uy * headLen;
  const left = `${ax + px * headLen * 0.5},${ay + py * headLen * 0.5}`;
  const right = `${ax - px * headLen * 0.5},${ay - py * headLen * 0.5}`;
  return `${left} ${s.x2},${s.y2} ${right}`;
};

// Rough hit-test for eraser. Returns true if (x,y) is within r of the shape.
const hitTest = (s, x, y, r2) => {
  if (s.type === "pen" || s.type === "highlight") {
    // Sample points along the path string and test distance
    const pts = (s.d.match(/-?\d+(?:\.\d+)?/g) || []).map(Number);
    for (let i = 0; i < pts.length - 1; i += 2) {
      const dx = x - pts[i], dy = y - pts[i + 1];
      if (dx * dx + dy * dy < r2) return true;
    }
    return false;
  }
  if (s.type === "line" || s.type === "arrow") {
    return distToSegment2(x, y, s.x1, s.y1, s.x2, s.y2) < r2;
  }
  if (s.type === "rect") {
    const x0 = Math.min(s.x1, s.x2), y0 = Math.min(s.y1, s.y2);
    const x1 = Math.max(s.x1, s.x2), y1 = Math.max(s.y1, s.y2);
    // 4 edges
    return (
      distToSegment2(x, y, x0, y0, x1, y0) < r2 ||
      distToSegment2(x, y, x1, y0, x1, y1) < r2 ||
      distToSegment2(x, y, x1, y1, x0, y1) < r2 ||
      distToSegment2(x, y, x0, y1, x0, y0) < r2
    );
  }
  if (s.type === "ellipse") {
    const cx = (s.x1 + s.x2) / 2, cy = (s.y1 + s.y2) / 2;
    const rx = Math.abs(s.x2 - s.x1) / 2, ry = Math.abs(s.y2 - s.y1) / 2 || 1;
    const dx = (x - cx) / (rx || 1), dy = (y - cy) / ry;
    const d = Math.abs(Math.sqrt(dx * dx + dy * dy) - 1) * Math.min(rx, ry);
    return d * d < r2;
  }
  return false;
};

const distToSegment2 = (px, py, x1, y1, x2, y2) => {
  const dx = x2 - x1, dy = y2 - y1;
  const len2 = dx * dx + dy * dy || 1;
  let t = ((px - x1) * dx + (py - y1) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const fx = x1 + dx * t, fy = y1 + dy * t;
  return (px - fx) ** 2 + (py - fy) ** 2;
};
